// client/components/Inventar/InventarBlockInfo.jsx
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Package } from "lucide-react";
import { showSuccess, showError } from "../../utils/toast.js";
import api from "../../constants/api";
import InventarStatusBadge from "./InventarStatusBadge";
import { INVENTAR_STATUS_OPTIONS } from "../../constants/inventarStatus";
import { Z_INDEX } from "../ui/zIndex.js";
import { Field } from "../forms/shared/FormAtoms.jsx";
import AppButton from "../ui/AppButton.jsx";
import {
  entityMoreMenuDangerItemClassName,
  entityMoreMenuItemEmojiClassName,
  entityMoreMenuPanelClassName,
  entityMoreMenuTriggerClassName,
  entityInfoBlockHeaderShellClassName,
  entityInfoBlockHeaderActionsClassName,
  entityInfoBlockEditButtonClassName,
} from "../../design/tokens.js";

function DarkPopover({ pos, onClose, children }) {
  const ref = useRef(null);
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    const onClick = (e) => {
      if (!ref.current?.contains(e.target)) onClose?.();
    };
    const onRecalc = () => onClose?.();
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onClick);
    window.addEventListener("resize", onRecalc, { passive: true });
    window.addEventListener("scroll", onRecalc, { passive: true });
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onClick);
      window.removeEventListener("resize", onRecalc);
      window.removeEventListener("scroll", onRecalc);
    };
  }, [onClose]);

  return ReactDOM.createPortal(
    <div
      ref={ref}
      style={{
        position: "fixed",
        top: pos.top,
        left: pos.left,
        width: pos.width,
        zIndex: Z_INDEX.dropdown,
      }}
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full rounded-xl border border-white/12 bg-[#081124]/95 ring-blue-300/10 backdrop-blur-md shadow-2xl ring-1 p-1 transition-all">
        {children}
      </div>
    </div>,
    document.body
  );
}

export default function InventarBlockInfo({
  item,
  onEdit,
  onDelete,
  onStatusSaved,
  canWrite = true,
  canDelete = true,
}) {
  const [status, setStatus] = useState(item?.status || "verfügbar");
  const statusBtnRef = useRef(null);
  const [statusOpen, setStatusOpen] = useState(false);
  const [statusPos, setStatusPos] = useState({ top: 0, left: 0, width: 240 });
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);

  useEffect(() => setStatus(item?.status || "verfügbar"), [item?.status]);

  useEffect(() => {
    const onDoc = (e) => {
      if (moreOpen && moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
    };
    const onEsc = (e) => {
      if (e.key === "Escape") setMoreOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [moreOpen]);

  const calcPos = (btnEl, width = 240) => {
    const r = btnEl.getBoundingClientRect();
    const vw = window.innerWidth || document.documentElement.clientWidth;
    const left = Math.max(8, Math.min(r.right - width, vw - width - 8));
    return { top: r.bottom + 8, left, width };
  };

  const openStatusMenu = () => {
    if (!statusBtnRef.current) return;
    setStatusPos(calcPos(statusBtnRef.current, 240));
    setStatusOpen(true);
  };

  const saveStatus = async (newStatus) => {
    if (!canWrite) return;
    try {
      await api.put(`/inventar/${item.id}`, { status: newStatus });
      setStatus(newStatus);
      setStatusOpen(false);
      showSuccess("Status gespeichert");
      onStatusSaved?.(newStatus);
    } catch (e) {
      console.error(e);
      showError("Status konnte nicht gespeichert werden");
    }
  };

  const mengeLabel =
    item?.menge != null && item?.menge !== ""
      ? String(item.menge)
      : "–";

  return (
    <section
      className={`rounded-2xl px-3 py-3 border border-default bg-surface text-primary app-frame ${
        moreOpen ? "relative z-[1300]" : ""
      }`}
    >
      <div className={entityInfoBlockHeaderShellClassName}>
        <h2 className="text-lg sm:text-xl font-semibold flex items-start gap-2 min-w-0 text-blue-100/90 max-md:text-base max-md:leading-snug sm:min-w-[12rem] sm:flex-1">
          <Package className="w-5 h-5 shrink-0 mt-0.5 max-md:w-4 max-md:h-4" aria-hidden />
          <span className="line-clamp-2">Inventardaten</span>
        </h2>

        <div className={entityInfoBlockHeaderActionsClassName}>
          {canWrite && (
            <AppButton
              type="button"
              onClick={() => onEdit?.(item)}
              variant="primary"
              size="sm"
              className={entityInfoBlockEditButtonClassName}
            >
              Bearbeiten
            </AppButton>
          )}
          {canDelete && (
            <div className="relative shrink-0" ref={moreRef}>
              <AppButton
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setMoreOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={moreOpen ? "true" : "false"}
                aria-label="Mehr Aktionen"
                title="Mehr Aktionen"
                className={entityMoreMenuTriggerClassName}
              >
                <span className="text-lg leading-none font-semibold px-1 tracking-wide select-none" aria-hidden>
                  ...
                </span>
              </AppButton>
              {moreOpen && (
                <div
                  role="menu"
                  aria-label="Mehr Aktionen"
                  className={entityMoreMenuPanelClassName}
                  style={{ zIndex: Z_INDEX.selectPortal }}
                >
                  <AppButton
                    type="button"
                    variant="ghost"
                    size="sm"
                    role="menuitem"
                    onClick={() => {
                      setMoreOpen(false);
                      onDelete?.(item);
                    }}
                    className={entityMoreMenuDangerItemClassName}
                  >
                    Löschen
                    <span className={entityMoreMenuItemEmojiClassName} aria-hidden>
                      🗑
                    </span>
                  </AppButton>
                </div>
              )}
            </div>
          )}
          {!canWrite && !canDelete && (
            <span className="text-xs text-blue-200/70 self-center">Nur lesend</span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 text-sm text-blue-100/90">
        <Field label="Bezeichnung" value={item?.bezeichnung || "–"} isDark={true} />
        <Field label="Hersteller" value={item?.hersteller || "–"} isDark={true} />
        <Field label="Seriennummer" value={item?.seriennummer || "–"} isDark={true} />
        <Field label="Typ/Kategorie" value={`${item?.projekttyp || "–"} / ${item?.kategorie || "–"}`} isDark={true} />
        <Field
          label="Standort"
          value={`${item?.standort_typ || "–"}${item?.standort_bez ? ` – ${item.standort_bez}` : ""}`}
          isDark={true}
        />
        <Field label="Menge" value={mengeLabel} isDark={true} />

        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wide text-blue-300/60">Status</div>
          <div className="mt-0.5 flex items-center gap-2 flex-wrap">
            <InventarStatusBadge status={status} />
            {canWrite && (
              <AppButton
                ref={statusBtnRef}
                onClick={openStatusMenu}
                type="button"
                variant="ghost"
                className="px-2 py-1 rounded-md border text-xs border-white/15 bg-white/5 hover:bg-white/10 text-blue-100"
                title="Status ändern"
              >
                Ändern
              </AppButton>
            )}
          </div>
          {statusOpen && canWrite && (
            <DarkPopover pos={statusPos} onClose={() => setStatusOpen(false)}>
              <div role="menu">
                {INVENTAR_STATUS_OPTIONS.map(({ value, label }) => (
                  <AppButton
                    key={value}
                    onClick={() => saveStatus(value)}
                    variant="ghost"
                    role="menuitem"
                    type="button"
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition min-h-0 !justify-start ${
                      status === value
                        ? "bg-[#0e1a34]/70 text-blue-100 border border-white/10"
                        : "text-blue-100/90 hover:bg-white/10 hover:text-blue-100"
                    }`}
                  >
                    {label} {status === value ? "•" : ""}
                  </AppButton>
                ))}
              </div>
            </DarkPopover>
          )}
        </div>

        <div className="md:col-span-2">
          <div className="text-xs uppercase tracking-wide text-blue-300/60">Notiz</div>
          <div className="mt-0.5 whitespace-pre-wrap">{item?.notiz || "–"}</div>
        </div>

        <div className="md:col-span-2">
          <div className="text-xs uppercase tracking-wide mb-1 text-blue-300/60">Kategorie-Details</div>
          {Object.entries(item?.details || {}).length === 0 ? (
            <div className="text-sm text-blue-200/70">– keine Details –</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              {Object.entries(item.details).map(([k, v]) => (
                <Field key={k} label={k} value={String(v ?? "–")} isDark={true} />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
